/**
 * JobMatchesPanel — "Matches for you": live openings AI-scored against the
 * user's resume. Shown wherever the resume is being reviewed (First
 * Impression, fit reports, Studio). One-click Tailor reuses the Job Search
 * → Feed Job prefill flow so the job thread stays connected end-to-end.
 * Header exposes the user's job prefs (city + remote) inline.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Crosshair, RefreshCw, ArrowRight, ExternalLink, Wand2, Loader,
  MapPin, Check, Sparkles,
} from "lucide-react";

const pctColor = (p) => (p >= 75 ? "#10B981" : p >= 60 ? "#FFC145" : "#94A3B8");
const REMOTE_LABELS = { any: "Any", remote: "Remote", hybrid: "Hybrid", onsite: "On-site" };

export default function JobMatchesPanel({ className = "", limit = 4 }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ location: "", remote: "any" });
  const [form, setForm] = useState({ location: "", remote: "any" });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const { data: d } = await api.get(`/job-matches${refresh ? "?refresh=1" : ""}`);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    api.get("/job-prefs")
      .then((r) => { setPrefs(r.data); setForm(r.data); })
      .catch(() => {});
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const { data: p } = await api.patch("/job-prefs", {
        location: form.location.trim(),
        remote: form.remote,
        salary_min: form.salary_min ? parseInt(form.salary_min, 10) : 0,
      });
      setPrefs(p);
      setPrefsOpen(false);
      await load(true);
    } catch { /* keep form open */ }
    finally { setSavingPrefs(false); }
  };

  const tailor = (m) => {
    navigate("/feed-job", {
      state: {
        prefill: {
          jobTitle: m.title || "",
          companyName: m.company || "",
          jobDescription: m.description || "",
          source: "adzuna",
          adzuna_url: m.url || "",
        },
      },
    });
  };

  if (loading) {
    return (
      <div className={`px-4 py-3 rounded-lg ${className}`}
           style={{ border: "2px dashed #0F1B2D", background: "#F8FAFC" }}
           data-testid="job-matches-loading">
        <div className="flex items-center gap-2 font-mono-rm text-[10px] uppercase tracking-widest font-bold"
             style={{ color: "#475569" }}>
          <Loader size={12} className="animate-spin" /> Hunting live matches for you…
        </div>
      </div>
    );
  }
  if (!data || !(data.matches || []).length) return null;

  const rows = data.matches.slice(0, limit);
  return (
    <div className={`p-4 rounded-lg ${className}`}
         style={{ border: "2px solid #0F1B2D", background: "white", boxShadow: "4px 4px 0 0 #0F1B2D" }}
         data-testid="job-matches-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 font-mono-rm text-[10px] uppercase tracking-widest font-bold"
             style={{ color: "#FF6A00" }}>
          <Crosshair size={12} /> · Matches for you
          <span className="normal-case tracking-normal px-1.5 py-0.5 rounded font-sans"
                style={{ background: "#F1F5F9", color: "#334155" }}
                data-testid="job-matches-role">
            {data.role}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => { setForm(prefs); setPrefsOpen((o) => !o); }}
                  className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                  title="Where should matches come from?"
                  data-testid="job-matches-prefs-btn">
            <MapPin size={11} />
            {prefs.location || "Anywhere"}
            {prefs.remote !== "any" ? ` · ${REMOTE_LABELS[prefs.remote]}` : ""}
            {prefs.salary_min > 0 ? ` · $${Math.round(prefs.salary_min / 1000)}k+` : ""}
          </button>
          <button onClick={() => load(true)} disabled={refreshing}
                  className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                  title="Re-scan live openings"
                  data-testid="job-matches-refresh">
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {prefsOpen && (
        <div className="mt-3 flex items-center gap-2 flex-wrap px-3 py-2 rounded"
             style={{ background: "#FFF7E6", border: "1px dashed #B45309" }}
             data-testid="job-matches-prefs-form">
          <input value={form.location}
                 onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                 placeholder="City (e.g. Denver) — blank = anywhere"
                 className="text-[12px] px-2 py-1 rounded flex-1 min-w-[160px]"
                 style={{ border: "1.5px solid #0F1B2D", background: "white" }}
                 data-testid="job-matches-prefs-city" />
          <select value={form.remote}
                  onChange={(e) => setForm((f) => ({ ...f, remote: e.target.value }))}
                  className="text-[12px] px-2 py-1 rounded"
                  style={{ border: "1.5px solid #0F1B2D", background: "white" }}
                  data-testid="job-matches-prefs-remote">
            <option value="any">Any setup</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
            <option value="onsite">On-site</option>
          </select>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px]" style={{ color: "#475569" }}>$</span>
            <input value={form.salary_min || ""}
                   onChange={(e) => setForm((f) => ({ ...f, salary_min: e.target.value.replace(/\D/g, "") }))}
                   placeholder="Min salary"
                   inputMode="numeric"
                   className="text-[12px] pl-5 pr-2 py-1 rounded w-[110px]"
                   style={{ border: "1.5px solid #0F1B2D", background: "white" }}
                   title="Only match jobs paying at least this — blank for all"
                   data-testid="job-matches-prefs-salary" />
          </div>
          <button onClick={savePrefs} disabled={savingPrefs}
                  className="rm-btn text-[11px] px-2.5 py-1"
                  style={{ background: "#0F1B2D", color: "white" }}
                  data-testid="job-matches-prefs-save">
            {savingPrefs ? <Loader size={11} className="animate-spin" /> : <Check size={11} />}
            {savingPrefs ? "Rescanning…" : "Save"}
          </button>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {rows.map((m, i) => (
          <div key={m.id || i}
               className="flex items-center gap-3 px-3 py-2 rounded"
               style={{ border: "1px solid #CBD5E1", background: "#FAFAF7" }}
               data-testid={`job-match-row-${i}`}>
            <span className="font-mono-rm text-[10px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                  style={{ background: pctColor(m.match_pct) }}
                  title={m.match_basis === "ai" ? "Scored by Resmo's AI against your resume" : "Keyword match estimate"}
                  data-testid={`job-match-pct-${i}`}>
              {m.match_pct}%
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold truncate" style={{ color: "#0F1B2D" }}>{m.title}</div>
              <div className="text-[11px] truncate" style={{ color: "#64748B" }}>
                {m.company}{m.location ? ` · ${m.location}` : ""}
              </div>
              {m.match_reason && (
                <div className="text-[11px] italic truncate flex items-center gap-1"
                     style={{ color: "#B45309" }}
                     title={m.match_reason}
                     data-testid={`job-match-reason-${i}`}>
                  <Sparkles size={10} className="flex-shrink-0" /> {m.match_reason}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => tailor(m)}
                      className="rm-btn text-[10px] px-2 py-1"
                      style={{ background: "#FF6A00", color: "white" }}
                      title="Tailor your resume for this job"
                      data-testid={`job-match-tailor-${i}`}>
                <Wand2 size={11} /> Tailor
              </button>
              {m.url && (
                <a href={m.url} target="_blank" rel="noopener noreferrer"
                   className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                   title="Open the posting"
                   data-testid={`job-match-view-${i}`}>
                  <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => navigate("/job-search")}
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold hover:underline"
              style={{ color: "#B45309", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              data-testid="job-matches-more">
        Hunt more live jobs <ArrowRight size={12} />
      </button>
    </div>
  );
}
